import { z } from "zod";

import { isPastDate } from "../date";
import { isObject } from "../type-guard";

const zErrorCode = z.enum(["DATE_MUST_NOT_BE_IN_THE_PAST"]);
const ErrorCode = zErrorCode.Enum;
type ErrorCode = z.infer<typeof zErrorCode>;

const INVALID_DATE = "invalid_date";
const CUSTOM_CODE = "customCode";

type InvalidDateIssue = Omit<z.ZodCustomIssue, "params"> & {
  params: {
    [CUSTOM_CODE]: typeof INVALID_DATE;
    errorCode: ErrorCode;
  };
};

function isInvalidDateIssue(value: z.ZodIssueOptionalMessage): value is InvalidDateIssue {
  return (
    value.code === z.ZodIssueCode.custom &&
    isObject(value.params) &&
    value.params[CUSTOM_CODE] === INVALID_DATE
  );
}

const CONFIG: Record<
  ErrorCode,
  {
    check: (value: Date) => boolean;
    errorMessage: string;
  }
> = {
  DATE_MUST_NOT_BE_IN_THE_PAST: {
    check: (value) => !isPastDate(value),
    errorMessage: "The date must not be in the past",
  },
};

function refine(errorCode: ErrorCode) {
  const { check } = CONFIG[errorCode];
  return (value: Date, ctx: z.RefinementCtx) => {
    if (!check(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        params: {
          customCode: INVALID_DATE,
          errorCode,
        } satisfies InvalidDateIssue["params"],
      });
    }
  };
}

const REFINEMENTS = {
  notInThePast: refine(ErrorCode.DATE_MUST_NOT_BE_IN_THE_PAST),
};

const errorMap: z.ZodErrorMap = (issue, ctx) => {
  if (isInvalidDateIssue(issue)) {
    return {
      message: CONFIG[issue.params.errorCode].errorMessage,
    };
  }

  return {
    message: ctx.defaultError,
  };
};

export { ErrorCode, errorMap, isInvalidDateIssue, REFINEMENTS };
export type { InvalidDateIssue };
